/* Copyright (c) 2025 MOCA Systems, Inc.  All rights reserved. */

import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	DestroyRef,
	inject,
	OnInit,
	Optional,
	Self,
} from '@angular/core';
import {
	ControlValueAccessor,
	FormBuilder,
	FormControl,
	NgControl,
	FormGroupDirective,
} from '@angular/forms';
import ticketValidator from '@touchplan/config/lib/ticket-validators.json';
import { DurationUtils } from '../../../../utils/duration-utils';
import * as strings from '@touchplan/strings';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { startWith } from 'rxjs';

@Component({
	selector: 'tp-duration-hours',
	templateUrl: './duration-hours.component.html',
	styleUrls: ['./duration-hours.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DurationHoursComponent implements ControlValueAccessor, OnInit {
	private formBuilder = inject(FormBuilder);
	private changeDetectorRef = inject(ChangeDetectorRef);
	private controlContainer = inject(FormGroupDirective);
	public enableErrors = false;

	public form = this.formBuilder.group({
		hours: new FormControl<number>(0),
		minutes: new FormControl<number>(0),
		seconds: new FormControl<number>(0),
	});

	private currentValue = '';

	public appStrings = strings;

	public validationData = ticketValidator;
	private readonly destroyRef = inject(DestroyRef);

	public onTouched!: () => void;
	private onValueChange!: (_: string) => void;

	constructor(@Self() @Optional() private ngControl: NgControl) {
		if (this.ngControl !== null) {
			// Setting the value accessor directly (instead of using
			// the providers) to avoid running into a circular import.
			this.ngControl.valueAccessor = this;
		}
	}

	public ngOnInit(): void {
		this.form.controls.hours.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
			this.onInternalChange();
			this.checkForErrors();
		});

		this.form.controls.minutes.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
			this.onInternalChange();
			this.checkForErrors();
		});

		this.controlContainer.ngSubmit.subscribe(() => {
			this.enableErrors = true; //once submitted, enable showing errors even if not touched
			this.checkForErrors();
		});

		this.ngControl.control?.statusChanges
			.pipe(startWith(this.ngControl.control?.status), takeUntilDestroyed(this.destroyRef))
			.subscribe((value) => {
				this.checkForErrors();
			});
	}

	/**
	 * Marks the fields with errors (will show red boreder) if there are errors on the control.
	 * @private
	 */
	private checkForErrors() {
		const errors = this.ngControl.control?.errors;
		if (
			errors &&
			(this.form.controls.hours.touched ||
				this.form.controls.minutes.touched ||
				this.controlContainer?.submitted)
		) {
			if (errors.invalidDurationHours || errors.invalidMaxDurationHours) {
				this.form.get('hours')?.setErrors({ parentError: true });
				this.form.get('hours')?.markAsTouched();

				this.form.get('minutes')?.setErrors({ parentError: true });
				this.form.get('minutes')?.markAsTouched();
				this.changeDetectorRef.detectChanges(); //needed for field to be styled in error after setting manually
				console.log(this.form);
			}
		} else if (!errors && (this.form.controls.hours.touched || this.form.controls.minutes.touched)) {
			this.form.get('hours')?.updateValueAndValidity({ onlySelf: true, emitEvent: false });
			this.form.get('minutes')?.updateValueAndValidity({ onlySelf: true, emitEvent: false });
		}
	}

	private onInternalChange(): void {
		if (this.onValueChange) {
			const hours = this.form.controls['hours'].value || 0;
			const minutes = this.form.controls['minutes'].value || 0;
			const seconds = this.form.controls['seconds'].value || 0;
			const value = DurationUtils.formatDurationHours(hours, minutes, seconds);
			if (this.currentValue !== value) {
				//only trigger if we actually changed the value
				this.currentValue = value;
				this.onValueChange(value);
				this.ngControl.control?.updateValueAndValidity();
			}
		}
	}

	/**
	 * WriteValue method of the ControlValueAccessor.
	 * Takes the value for duration hours in HH:mm:ss format and sets the hour and minute values with them
	 * for the internal form fields.
	 * @param value duration hours in HH:mm:ss format
	 */
	public writeValue(value: string): void {
		if (value && value !== this.currentValue) {
			this.currentValue = value;
			const { hours, minutes, seconds } = DurationUtils.getHoursMinutesSeconds(value);
			this.form.patchValue({ hours, minutes, seconds });
		}
	}

	/**
	 * ControlValueAccessor function:
	 * This method is called by Angular to give this class a callback method, which is to be called
	 * when the (externally visible) value of this component should be updated.
	 */
	public registerOnChange(fn: (_: string) => void): void {
		this.onValueChange = fn;
	}

	/**
	 * ControlValueAccessor function:
	 * This is another method that registers a callback function.  This one needs to be called when this
	 * component is "touched".  We track that via the "(blur)" event handler in the HTML template, which
	 * calls the "onBlur()" method.  That in turn calls the callback method provided as a parameter here.
	 */
	public registerOnTouched(fn: () => void): void {
		this.onTouched = () => {
			this.checkForErrors();
			fn();
		};
	}
}
